/**
 * Global Error Boundary — Design System §11 (Error States)
 *
 * Dark-first tokenized fallback with voice-spec copy. Previously rendered on
 * the legacy amber-on-white palette with a 🌮 emoji; now uses the TacoMark
 * SVG brand, dark surface tokens, and copy that owns the moment without
 * being corporate ("hit a snag" → "The chef dropped a plate").
 */
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import { TacoMark } from './atoms/TacoMark';

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div
      role="alert"
      className="min-h-screen flex items-center justify-center p-8 ts-page-enter"
      style={{ background: 'var(--ts-bg-base)', color: 'var(--ts-text-primary)' }}
      data-testid="error-boundary-fallback"
    >
      <div className="max-w-md text-center space-y-6">
        <div className="flex justify-center">
          <TacoMark size={64} />
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight"
          style={{ fontFamily: 'var(--ts-font-display)', letterSpacing: '-0.02em' }}
        >
          The chef dropped a plate.
        </h1>
        <p style={{ color: 'var(--ts-text-secondary)', fontSize: 15, lineHeight: 1.55 }}>
          Something broke in the kitchen, not in your data. Saved apartments, notes,
          and account info are all safe.
        </p>
        <div
          className="rounded-lg p-3 text-left"
          style={{
            background: 'var(--ts-bg-inset)',
            border: '1px solid var(--ts-border-subtle)',
          }}
        >
          <p
            className="text-xs font-mono break-words"
            style={{ color: 'var(--ts-text-tertiary, var(--ts-text-secondary))' }}
          >
            {(error as Error).message || 'An unexpected error occurred'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <button
            onClick={resetErrorBoundary}
            className="px-5 py-2.5 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
            style={{
              background: 'var(--ts-accent-primary)',
              color: 'var(--ts-accent-contrast, #0b0b0c)',
            }}
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="px-5 py-2.5 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
            style={{
              background: 'var(--ts-surface)',
              color: 'var(--ts-text-primary)',
              border: '1px solid var(--ts-border-subtle)',
            }}
            data-testid="error-boundary-home"
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.reload();
      }}
      onError={(error, info) => {
        console.error('Error caught by boundary:', error);
        console.error('Component stack:', info.componentStack);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export default AppErrorBoundary;
